/**
 * Strings of the "Deliveries" tab in Customer 360 (`custDeliveries.*`), merged into the dictionary through `extra.ts`.
 * `custDeliveriesEn` is typed against `custDeliveriesPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const custDeliveriesPl = {
  tab: 'Dostawy',
  title: 'Ostatnie dostawy',
  subtitle: 'Dostawy do tego klienta z ostatnich 12 miesięcy',
  openInLogistics: 'Otwórz w Logistyce',
  truncated: 'Pokazano {shown} z {total} dostaw — pełną listę znajdziesz w Logistyce.',
  kpi: {
    total: 'Dostawy (12 mies.)',
    open: 'Do zrealizowania: {count}',
    onTime: 'Terminowość',
    onTimeHint: 'Dostarczone w terminie: {onTime} z {delivered}',
    onTimeNone: 'Brak dostarczonych dostaw',
    failed: 'Nieudane',
    failedHint: 'Dostawy, które się nie odbyły',
    late: 'Spóźnione',
    lateHint: 'Po terminie planu, także te w drodze',
  },
  col: {
    date: 'Dzień planu',
    status: 'Status',
    route: 'Trasa',
    delay: 'Spóźnienie',
    reason: 'Powód',
    document: 'Dokument',
  },
  status: {
    PLANNED: 'Zaplanowana',
    IN_TRANSIT: 'W drodze',
    DELIVERED: 'Dostarczona',
    FAILED: 'Nieudana',
    CANCELLED: 'Anulowana',
  },
  reason: {
    NO_ONE_AT_SITE: 'Nikogo na miejscu',
    REFUSED: 'Odmowa przyjęcia',
    WRONG_ADDRESS: 'Błędny adres',
    DAMAGED_GOODS: 'Uszkodzony towar',
    NO_PAYMENT: 'Brak płatności',
    VEHICLE_FAILURE: 'Awaria pojazdu',
    TIME_EXCEEDED: 'Przekroczony czas',
    OTHER: 'Inny powód',
  },
  delayDays: '{days} dni',
  delayDay: '1 dzień',
  onSchedule: 'w terminie',
  noRoute: 'bez trasy',
  empty: 'Brak dostaw w ostatnich 12 miesiącach',
  emptyBody: 'Gdy dostawy do tego klienta pojawią się w Logistyce, zobaczysz je tutaj.',
};

export const custDeliveriesEn: Shape<typeof custDeliveriesPl> = {
  tab: 'Deliveries',
  title: 'Recent deliveries',
  subtitle: 'Deliveries to this customer over the last 12 months',
  openInLogistics: 'Open in Logistics',
  truncated: 'Showing {shown} of {total} deliveries — the full list is in Logistics.',
  kpi: {
    total: 'Deliveries (12 mo.)',
    open: 'Still to deliver: {count}',
    onTime: 'On-time rate',
    onTimeHint: 'Delivered on time: {onTime} of {delivered}',
    onTimeNone: 'No delivered deliveries',
    failed: 'Failed',
    failedHint: 'Deliveries that did not take place',
    late: 'Late',
    lateHint: 'Past the planned day, including those in transit',
  },
  col: {
    date: 'Planned day',
    status: 'Status',
    route: 'Route',
    delay: 'Delay',
    reason: 'Reason',
    document: 'Document',
  },
  status: {
    PLANNED: 'Planned',
    IN_TRANSIT: 'In transit',
    DELIVERED: 'Delivered',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
  },
  reason: {
    NO_ONE_AT_SITE: 'No one at the site',
    REFUSED: 'Refused',
    WRONG_ADDRESS: 'Wrong address',
    DAMAGED_GOODS: 'Damaged goods',
    NO_PAYMENT: 'No payment',
    VEHICLE_FAILURE: 'Vehicle failure',
    TIME_EXCEEDED: 'Time exceeded',
    OTHER: 'Other reason',
  },
  delayDays: '{days} days',
  delayDay: '1 day',
  onSchedule: 'on time',
  noRoute: 'no route',
  empty: 'No deliveries in the last 12 months',
  emptyBody: 'Once deliveries to this customer show up in Logistics, you will see them here.',
};
